// frontend/src/components/Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '@/firebase';
import {
  Film, LogOut, Bookmark, MessageSquare, Search, User,
  Home, Compass, Tv, ChevronDown, X, Loader2, Menu
} from 'lucide-react';
import NotificationMenu from './NotificationMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// "Community" is now a primary global link
const PRIMARY_NAV = [
  { path: '/',          label: 'Feed',      Icon: Home },
  { path: '/discover',  label: 'Discover',  Icon: Compass },
  { path: '/movie',     label: 'Movies',    Icon: Film },
  { path: '/tv',        label: 'TV',        Icon: Tv },
  { path: '/community', label: 'Community', Icon: MessageSquare },
];

export default function Navbar() {
  const [user, setUser]               = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch]   = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]     = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobile, setShowMobile]   = useState(false);

  const navigate      = useNavigate();
  const location      = useLocation();
  const userMenuRef   = useRef(null);
  const searchTimeout = useRef(null);

  // ── Auth ──────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        return;
      }

      try {
        const token = await firebaseUser.getIdToken();
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setUser({
          ...res.data,
          email: firebaseUser.email,
          picture: res.data?.picture || firebaseUser.photoURL || '',
          name: res.data?.name || firebaseUser.displayName || 'User',
        });
      } catch (err) {
        console.error('Failed to sync profile:', err);
        setUser({
          name: firebaseUser.displayName || 'User',
          picture: firebaseUser.photoURL || '',
          karma: 0,
        });
      }
    });

    return () => unsubscribe();
  }, []);

  // ── Close dropdowns on outside click ──────────────────────
  useEffect(() => {
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Close mobile menu on route change ─────────────────────
  useEffect(() => { setShowMobile(false); }, [location.pathname]);

  // ── Inline search (live results) ──────────────────────────
  const handleSearchInput = (val) => {
    setSearchQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!val.trim()) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/search?query=${encodeURIComponent(val)}`);
        setSearchResults((res.data.results || res.data.movies || []).slice(0, 6));
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      closeSearch();
    }
  };

  const closeSearch = () => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 h-16 bg-background/92 backdrop-blur-xl border-b border-white/6">
      <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex items-center gap-4">

        {/* ── GEN-Z LOGO ─────────────────────────────────────────── */}
        <Link
          to="/"
          className="flex items-center gap-2.5 flex-shrink-0 mr-2 group"
        >
          {/* Custom SVG: Rewind-Smiley Clapperboard */}
          <svg width="34" height="34" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="transform group-hover:scale-110 transition-transform duration-300">
            <defs>
              <linearGradient id="sunsetGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#c084fc" />   {/* Purple */}
                <stop offset="50%" stopColor="#f43f5e" />  {/* Rose */}
                <stop offset="100%" stopColor="#f97316" /> {/* Orange */}
              </linearGradient>
            </defs>
            {/* Clapperboard Body */}
            <rect x="12" y="32" width="76" height="56" rx="14" stroke="url(#sunsetGrad)" strokeWidth="8" />
            {/* Clapperboard Top Hinge */}
            <path d="M12 24 L88 12" stroke="url(#sunsetGrad)" strokeWidth="8" strokeLinecap="round" />
            <path d="M20 22 L28 8 M40 19 L48 5 M60 16 L68 2" stroke="url(#sunsetGrad)" strokeWidth="6" strokeLinecap="round" />
            {/* Eyes: < > */}
            <polyline points="34,48 24,56 34,64" stroke="url(#sunsetGrad)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="66,48 76,56 66,64" stroke="url(#sunsetGrad)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
            {/* Smile: Film Strip Curve */}
            <path d="M 34,76 Q 50,88 66,76" stroke="url(#sunsetGrad)" strokeWidth="6" strokeLinecap="round" fill="none" />
            {/* Film Strip Holes */}
            <line x1="42" y1="78" x2="42" y2="82" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="text-background" />
            <line x1="50" y1="80" x2="50" y2="84" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="text-background" />
            <line x1="58" y1="78" x2="58" y2="82" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="text-background" />
          </svg>
          
          {/* Custom Typography */}
          <span className="text-2xl font-black tracking-tighter hidden sm:block bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-rose-500 to-orange-500 pr-1">
            Aftercredits
          </span>
        </Link>

        {/* ── PRIMARY NAV (Desktop Left) ───────────────────── */}
        <div className="hidden md:flex items-center gap-1 flex-1">
          {PRIMARY_NAV.map(({ path, label, Icon }) => (
            <Link
              key={path}
              to={path}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                isActive(path)
                  ? 'bg-primary/15 text-primary'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon className="w-4 h-4" />{label}
            </Link>
          ))}
        </div>

        {/* ── RIGHT SIDE (Personal Actions) ────────────────── */}
        <div className="flex items-center gap-2 ml-auto">

          {/* Search */}
          <div className="relative">
            {showSearch ? (
              <form onSubmit={handleSearchSubmit}>
                <div className="flex items-center gap-2 bg-white/8 border border-white/15 rounded-full px-4 py-2 w-48 sm:w-64 focus-within:border-primary/50 transition-all">
                  {searching
                    ? <Loader2 className="w-4 h-4 text-muted-foreground animate-spin flex-shrink-0" />
                    : <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  }
                  <input
                    autoFocus
                    type="text"
                    value={searchQuery}
                    onChange={e => handleSearchInput(e.target.value)}
                    placeholder="Search…"
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none min-w-0"
                  />
                  <button type="button" onClick={closeSearch}
                    className="text-muted-foreground hover:text-white transition-colors flex-shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setShowSearch(true)}
                className="p-2 text-muted-foreground hover:text-white transition-colors rounded-xl hover:bg-white/5"
              >
                <Search className="w-5 h-5" />
              </button>
            )}

            {/* Live search results dropdown */}
            {searchResults.length > 0 && showSearch && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden">
                {searchResults.map(item => (
                  <Link
                    key={item.id}
                    to={`/${item.media_type === 'tv' ? 'tv' : 'movie'}/${item.id}`}
                    onClick={closeSearch}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                  >
                    {item.poster_path ? (
                      <img
                        src={`https://image.tmdb.org/t/p/w92${item.poster_path}`}
                        alt={item.title || item.name}
                        className="w-8 h-12 rounded object-cover border border-white/10 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-12 rounded bg-muted flex items-center justify-center flex-shrink-0">
                        <Film className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white line-clamp-1">{item.title || item.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {item.media_type === 'tv' ? 'TV Show' : 'Movie'}
                        {(item.release_date || item.first_air_date) && (
                          <> · {(item.release_date || item.first_air_date).substring(0, 4)}</>
                        )}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* ── Option 2: The Personal Action Menu ── */}
          {user ? (
            <>
              {/* Dedicated "My Lists" Button */}
              <Link 
                to="/my-list"
                title="My Lists"
                className={`px-3 py-2 transition-all rounded-xl flex items-center gap-2 border ${
                  isActive('/my-list') 
                    ? 'text-primary bg-primary/15 border-primary/30 shadow-lg shadow-primary/10' 
                    : 'text-white bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/25'
                }`}
              >
                <Bookmark className="w-5 h-5" />
                <span className="hidden sm:inline text-sm font-semibold">My Lists</span>
              </Link>

              {/* Notifications */}
              <NotificationMenu />

              {/* User Avatar Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(prev => !prev)}
                  className="flex items-center gap-2 p-1 ml-1 rounded-full hover:bg-white/5 transition-colors"
                >
                  <img
                    src={user.picture || 'https://ui-avatars.com/api/?name=User&background=111827&color=fff'}
                    alt={user.name}
                    className="w-8 h-8 rounded-full border-2 border-primary/20"
                  />
                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground hidden sm:block transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/8">
                      <p className="text-sm font-semibold text-white truncate">{user.name}</p>
                      <div className="mt-1.5 text-xs bg-primary/20 text-primary px-2 py-1 rounded inline-block font-mono">
                        Karma: {user.karma || 0}
                      </div>
                    </div>

                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <User className="w-4 h-4" /> My Profile
                    </Link>

                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link
              to="/login"
              className="ml-2 px-5 py-2 bg-primary text-primary-foreground rounded-full text-sm font-bold hover:bg-primary/90 transition-all shadow-md shadow-primary/20"
            >
              Sign in
            </Link>
          )}

          {/* Mobile hamburger */}
          <button
            onClick={() => setShowMobile(prev => !prev)}
            className="md:hidden p-2 text-muted-foreground hover:text-white transition-colors rounded-xl hover:bg-white/5 ml-1"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ── MOBILE MENU ──────────────────────────────────────── */}
      {showMobile && (
        <div className="md:hidden absolute top-16 left-3 right-3 rounded-2xl bg-black/95 backdrop-blur-2xl border border-white/15 z-40 py-3 px-3 space-y-1 shadow-2xl shadow-black/40">
          {PRIMARY_NAV.map(({ path, label, Icon }) => (
            <Link
              key={path}
              to={path}
              className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold transition-colors ${
                isActive(path)
                  ? 'bg-primary/15 text-primary'
                  : 'text-gray-200 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />{label}
            </Link>
          ))}
          {user && (
            <>
              <div className="h-px bg-white/10 my-2 mx-2" />
              <Link to="/my-list"
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold transition-colors ${
                  isActive('/my-list')
                    ? 'bg-primary/20 text-primary border border-primary/30'
                    : 'text-white bg-white/5 hover:bg-white/10 border border-white/10'
                }`}>
                <Bookmark className="w-4 h-4" /> My Lists
              </Link>
              <Link to="/profile"
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold text-gray-200 hover:bg-white/10 hover:text-white transition-colors">
                <User className="w-4 h-4" /> My Profile
              </Link>
              <button onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold text-red-300 hover:bg-red-500/15 transition-colors">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
